import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Modal, Spinner } from 'react-bootstrap'
import cn from 'classnames'

import Title from '../components/ui/Title/Title'
import Image from '../components/ui/Image/Image'

import { getImageUrl, isArabic } from '../utils'
import ArrowRight from '../assets/images/ArrowRight.svg'
import activeDownArrow from '../assets/images/active-down-arrow.svg'
import downArrow from '../assets/images/down-arrow.svg'
import styles from './Header.module.scss'
import './ModalStyles.scss'
import { useLanguage } from '../context/LangaugeContextProvider/LangaugeContextProvider'

const Menu = ({
  show,
  handleClose,
  menus = [],
  loading = true,
  error,
  detailsSection,
  selectedPath,
}) => {
  let { pathname } = useLocation()
  const navigate = useNavigate()
  const [, firstLevelPath, secondLevelPath] = pathname.split('/')
  const [menuIndex, setMenuIndex] = useState(-1)
  const [secondMenuKey, setSecondMenuKey] = useState(
    !selectedPath ? secondLevelPath : -1
  )
  const { language } = useLanguage()

  useEffect(() => {
    if (menus && menus.length > 0) {
      const getMenuIndex = (menuKey) =>
        menus.findIndex((menu) => menu?.link?.path?.split('/')[1] === menuKey)
      setMenuIndex(
        getMenuIndex(selectedPath ? selectedPath.split('/')[1] : firstLevelPath)
      )
    }
  }, [menus, firstLevelPath, selectedPath])

  const secondLevelMenu = useMemo(() => {
    return menuIndex >= 0 ? menus[menuIndex].menus : []
  }, [menuIndex, menus])

  const thirdLevelMenu = useMemo(() => {
    if (!secondLevelMenu.length) {
      return []
    }

    const foundMenu = secondLevelMenu.find((menu) => {
      return secondMenuKey && menu?.link?.path.includes(secondMenuKey)
    })
    return foundMenu ? foundMenu.menus : []
  }, [secondMenuKey, secondLevelMenu])

  const navigateTo = (url) => {
    navigate(url)
    handleClose()
  }

  const handleMenuClick = (index, type, url) => {
    if (type === 'link') {
      navigateTo(url)
    } else {
      setSecondMenuKey('')
      setMenuIndex(index)
    }
  }

  const handleSubMenuClick = (e, url, type) => {
    e.stopPropagation()
    if (type === 'link') {
      navigateTo(url)
    } else {
      setSecondMenuKey(url)
    }
  }

  const handleRouteClick = (url) => {
    navigateTo(url)
  }

  const handleDetailsClick = (details) => {
    navigateTo(details?.link?.path || '')
  }

  const renderMobileMenus = (menu, index) => {
    return (
      <div
        key={menu?.type + menu?.id}
        className={cn(styles.parentMenu, {
          // eslint-disable-next-line no-useless-computed-key
          ['activeText']: menuIndex === index,
        })}
        onClick={() => handleMenuClick(index, menu.type, menu?.link?.path)}
      >
        <div
          className={cn(styles.menuTextContainer, {
            // eslint-disable-next-line no-useless-computed-key
            ['activeText']: menuIndex === index,
          })}
        >
          {menu?.link?.label}
          {menu.type !== 'link' && (
            <img
              src={menuIndex === index ? activeDownArrow : downArrow}
              alt={'Expand'}
              className={cn('ml-1', {
                // eslint-disable-next-line no-useless-computed-key
                ['rotate180']: menuIndex === index,
              })}
              height={28}
              width={28}
            />
          )}
        </div>
        {menuIndex === index && secondLevelMenu && secondLevelMenu.length ? (
          <div className={cn(styles.mobileSubMenu)}>
            <div
              className={cn({
                [styles.mobileContainer]: menuIndex === index,
              })}
            >
              {secondLevelMenu.map((subMenu) => (
                <div
                  key={subMenu?.type + subMenu?.id}
                  className={cn(styles.levelTwoMenu)}
                  onClick={(e) =>
                    handleSubMenuClick(e, subMenu?.link?.path, subMenu.type)
                  }
                >
                  <div
                    className={cn(styles.menuTextContainer, {
                      // eslint-disable-next-line no-useless-computed-key
                      ['activeText']:
                        secondMenuKey &&
                        subMenu?.link?.path.includes(secondMenuKey),
                    })}
                  >
                    <span>{subMenu?.link?.label}</span>
                    {subMenu.type === 'menu' && (
                      <img
                        src={
                          secondMenuKey &&
                          subMenu?.link?.path.includes(secondMenuKey)
                            ? activeDownArrow
                            : downArrow
                        }
                        alt={'Expand'}
                        className={cn({
                          // eslint-disable-next-line no-useless-computed-key
                          ['rotate180']:
                            secondMenuKey &&
                            subMenu?.link?.path.includes(secondMenuKey),
                        })}
                        height={20}
                        width={20}
                      />
                    )}
                  </div>
                  {secondMenuKey &&
                  subMenu?.link?.path.includes(secondMenuKey) &&
                  thirdLevelMenu &&
                  thirdLevelMenu.length ? (
                    <div className={cn(styles.mobileSubMenu)}>
                      <div className={cn(styles.childContainer)}>
                        {thirdLevelMenu.map((childMenu) => (
                          <span
                            key={childMenu?.type + childMenu?.id}
                            onClick={() =>
                              handleRouteClick(childMenu?.link?.path)
                            }
                          >
                            {childMenu?.link?.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    )
  }

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        className={cn('m-0 p-0 w-full', styles.modalStyles)}
        dialogClassName="w-full h-full m-0"
        contentClassName={cn('w-full h-full p-0', styles.modalContent, {
          [styles.loading]: loading,
        })}
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            <Modal.Body className="p-0 w-full h-full">
              <div className="h-full flex flex-col">
                <div className={cn(styles.menusContainer)}>
                  <div className={cn(styles.mobileMainMenu)}>
                    {menus.length > 0 &&
                      menus.map((menu, index) =>
                        renderMobileMenus(menu, index)
                      )}
                  </div>
                  <div
                    className={cn(styles.mainMenu, {
                      // eslint-disable-next-line no-useless-computed-key
                      ['items-center']: menuIndex === 0,
                      [styles.arabic_mainMenu]: isArabic(language?.code),
                    })}
                  >
                    {menus.length > 0 &&
                      menus.map((menu, index) => (
                        <div
                          key={menu.type + menu?.id}
                          className={cn(styles.levelOneMenu, {
                            // eslint-disable-next-line no-useless-computed-key
                            ['activeText']: menuIndex === index,
                          })}
                          onClick={() =>
                            handleMenuClick(index, menu.type, menu?.link?.path)
                          }
                        >
                          {menu?.link?.label}
                          {menuIndex === index && menu.type !== 'link' && (
                            <img
                              src={ArrowRight}
                              alt={'Expand'}
                              className="ml-2"
                              height={28}
                              width={28}
                            />
                          )}
                        </div>
                      ))}
                  </div>
                  {menuIndex !== 0 && (
                    <div
                      className={cn(styles.subMenu, {
                        [styles.borderRight]:
                          !detailsSection && !isArabic(language?.code),
                        [styles.arabic_borderRight]:
                          !detailsSection && isArabic(language?.code),
                      })}
                    >
                      <div
                        className={cn(styles.subMenuContainer, {
                          [styles.noBorder]: !detailsSection,
                        })}
                      >
                        {secondLevelMenu && secondLevelMenu.length ? (
                          secondLevelMenu.map((subMenu) => (
                            <div
                              key={subMenu?.type + subMenu?.id}
                              className={cn(styles.levelTwoMenu, {
                                [styles.activeLevelTwo]:
                                  secondMenuKey &&
                                  subMenu?.link?.path.includes(secondMenuKey),
                              })}
                              onClick={(e) =>
                                handleSubMenuClick(
                                  e,
                                  subMenu?.link?.path,
                                  subMenu.type
                                )
                              }
                            >
                              {subMenu?.link?.label}
                              {secondMenuKey &&
                                subMenu.type === 'menu' &&
                                subMenu?.link?.path.includes(secondMenuKey) && (
                                  <img
                                    src={ArrowRight}
                                    alt={'Expand'}
                                    className="ml-1"
                                    height={20}
                                    width={20}
                                  />
                                )}
                            </div>
                          ))
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                  )}
                  {menuIndex !== 0 && (
                    <div className={cn(styles.subMenu)}>
                      <div
                        className={cn(styles.subMenuContainer, {
                          [styles.noBorder]: !detailsSection,
                        })}
                      >
                        {thirdLevelMenu && thirdLevelMenu.length ? (
                          thirdLevelMenu.map((subMenu, index) => (
                            <span
                              key={subMenu?.type + subMenu?.id}
                              className={cn(styles.levelTwoMenu)}
                              onClick={() =>
                                handleRouteClick(subMenu?.link?.path)
                              }
                            >
                              {subMenu?.link?.label}
                            </span>
                          ))
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                  )}
                  {detailsSection && (
                    <div className={cn(styles.menuContentSection)}>
                      <Title>{detailsSection?.title}</Title>
                      {detailsSection?.image && (
                        <Image
                          className={styles.detailsImage}
                          src={getImageUrl(detailsSection?.image)}
                          alt={detailsSection?.image?.alternativeText}
                          onClick={() => handleDetailsClick(detailsSection)}
                        />
                      )}
                      <p>{detailsSection?.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </Modal.Body>
          </>
        )}
      </Modal>
    </>
  )
}

export default Menu
